<template>
	<div class="data-chip" :class="[config.class, { 'is-selected': selected, 'is-data-deleted': !chipExists, 'table-param': isTableParam }]">
		<v-menu v-model="isOpen" :minWidth="100" offset-y dense :close-on-content-click="false" v-if="chipExists">
			<template v-slot:activator="{ on, attrs }">
				<div class="content-display" v-bind="attrs" @click.stop="(type, ...args) => showPopover(on, type, ...args)">
					<DataChipContent
						:chipSource="getChipData.fieldKey"
						:chipSample="chipSample"
						:nodeInfo="getChipData.app"
						:config="config"
						:itemValue="getChipData.param"
						:serviceSlug="getChipData.app?.service_slug"
						:isTableParam="isTableParam"
						:isFocused="isOpen"
						:isSelected="selected"
						:isAutofilled="isAutofilled"
						@autofill-rejected="handleAutofillRejected"
					/>
				</div>
			</template>
			<DataItemInfoCard
				:infoCardTop="1"
				:itemKey="itemKey"
				:item="getFormattedChipData"
				:itemValue="getChipData.param"
				:stepIndex="getChipData.step - 1"
				:formulaData="contentObj.formulaData"
			/>
		</v-menu>
		<DataMissingChip v-else :contentObj="contentObj" />
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';

import DataChipBridge from './data-chip-bridge';
import DataChipContent from './DataChipContent';
import DataItemInfoCard from '@/components/DataInput/DataInputMenu/MenuLists/helpers/DataItemInfoCard';
import ParamsService from '@/utils/ParamsService';
import { replaceInsertDataStringDictionary, getNodeByUuidForSelectedWorkflowVersion } from '@/store/modules/selectedWorkflow/getters';
import DataMissingChip from './DataMissingChip.vue';
import { configurationsMappingBlocksUUIDs } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { isValueFromAutofill } from '@/store/modules/selectedNode/getters';
import { rejectAutofill } from '@/store/modules/selectedNode/actions';

const CHIP_NOT_FOUND = 'chipNotFound';

export default {
	name: 'DataChip',
	components: {
		DataItemInfoCard,
		DataChipContent,
		DataMissingChip,
	},
	props: {
		contentObj: { type: Object },
		selected: { type: Boolean },
	},
	computed: {
		...mapGetters([
			getNodeByUuidForSelectedWorkflowVersion.fullPath,
			replaceInsertDataStringDictionary.fullPath,
			configurationsMappingBlocksUUIDs.fullPath,
		]),
		...mapGetters(['selectedNode/insertedDataDisplayMode', 'selectedNode/insertedDataDisplayModeMap']),
		...mapGettersShorthand({ isValueFromAutofill }),
		chipExists() {
			// Chip might exist though the data source has been erased by the user
			return this.getChipData !== CHIP_NOT_FOUND && this.getChipData !== null;
		},
		nodeService() {
			return this.getChipData.app;
		},
		isTableParam() {
			return this.contentObj.mode === 2;
		},
		config() {
			return this.contentObj?.config || {};
		},
		content() {
			return this.contentObj?.content;
		},
		paramKey() {
			return this.contentObj?.paramKey;
		},
		getChipData() {
			const {
				content,
				nodeId,
				options,
				getConvertedContent,
				config: { isRichTextEnabled },
			} = this.contentObj;
			try {
				const interpolatedContent = !this.isTableParam
					? DataChipBridge.getChipData(content, nodeId, options, isRichTextEnabled)
					: DataChipBridge.getChipDataFromMultiNode(content, nodeId, options, isRichTextEnabled);

				if (!interpolatedContent) {
					return CHIP_NOT_FOUND;
				}

				interpolatedContent.param = getConvertedContent ? getConvertedContent(interpolatedContent.param) : interpolatedContent.param;
				return interpolatedContent;
			} catch (err) {
				return CHIP_NOT_FOUND;
			}
		},
		getFormattedChipData() {
			return { ...this.getChipData, nodeInfo: this.nodeInfo, key: this.nodeUUID };
		},
		chipSample() {
			// TODO hack for now, until we know how and what to show as an example value of column in a row.. need some product work
			return this.isTableParam ? DataChipBridge.getChipDataProcessedCode(this.content) : this.getChipData.param;
		},
		nodeUUID() {
			return ParamsService.extractNodeUUIDFromValue(this.wrappedContent);
		},
		nodeInfo() {
			return this[getNodeByUuidForSelectedWorkflowVersion.fullPath](this.nodeUUID);
		},
		wrappedContent() {
			return `{{${this.content}}}`;
		},
		itemKey() {
			return this.isTableParam
				? DataChipBridge.getChipDataProcessedCode(this.content)
				: ParamsService.extractPathFromValue(
						this.wrappedContent,
						this[replaceInsertDataStringDictionary.fullPath],
						this[configurationsMappingBlocksUUIDs.fullPath]
				  );
		},
		isAutofilled() {
			return this.paramKey && this.isValueFromAutofill({ paramKey: this.paramKey, value: this.wrappedContent });
		},
	},
	methods: {
		...mapActionsShorthand({ rejectAutofill }),
		showPopover(on, type, ...args) {
			on.click(type, ...args);
		},
		async handleAutofillRejected() {
			await this.rejectAutofill({ paramKey: this.paramKey, rejectedValue: this.wrappedContent });
			this.$emit('autofill-rejected');
		},
	},
	data() {
		return { isOpen: false };
	},
};
</script>

<style scoped lang="scss">
$chip-border-radius: 3px;

.data-input-item-info-card-container {
	display: flex;
}
.data-chip {
	position: relative;
	display: inline;
	margin: 1rem 2px 1rem 0;
	border: none;
	border-radius: $chip-border-radius;
	overflow-wrap: anywhere; // Dont let it overflow

	&.table-param {
		&:hover {
			background: #ccc1f5;
			transition: 0.2s;
		}
		&:not(:hover) {
			background: var(--table-column-background);
			transition: 0.2s;
		}
		&.is-selected {
			background: #997104;
			transition: 0.2s;
		}
	}
	&.is-data-deleted {
		background: #ffc7b6;

		&:hover {
			background: #f7b8a4;
			transition: 0.2s;
		}
	}
	.content-display {
		position: relative;
		display: inline;
		border-radius: $chip-border-radius;
	}
}
</style>
